import { getAiModelSelectOptions, getDefaultAiModelId, resolveAiModelId } from "@fitness-app/data-models/ai/tasks";
import { useEffect, useState, type FunctionComponent, type ReactElement } from "react";
import { ThunderboltOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
  type FormInstance,
} from "antd";
import orderBy from "lodash.orderby";
import { v4 as uuid } from "uuid";

import {
  toAiNutritionPlans,
  toAiTrainingPrograms,
  type AiNutritionPlanConfig,
  type AiTrainingProgramConfig,
} from "@fitness-app/data-models/entities/ProgramAutomation";
import { programAutomationActions } from "@fitness-app/app-store";

import { useLoading } from "~/hooks/useLoading";
import { useMealPlanOptions } from "~/modules/Nutrition/hooks/useMealPlanOptions";
import {
  MealsPlanTemplateFilterFields,
  MealsPlanTemplateSelect,
} from "~/modules/Nutrition/components/MealsPlanTemplateSelect";
import {
  DEFAULT_MEALS_PLAN_TEMPLATE_FILTER,
  useMealsPlanTemplateSearch,
} from "~/modules/Nutrition/hooks/useMealsPlanTemplateSearch";
import ScheduledNutritionForm from "~/modules/Automation/ProgramAutomation/components/ScheduledNutrition/ScheduledNutritionForm";
import { useTrainingPlanSearch } from "~/modules/TrainingPrograms/hooks/useTrainingPlanSearch";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

// TRE-1603 (AI plan/diet automation v2) — AI scheduling merged into the existing
// section flows: the single "Dodaj kolejny plan/harmonogram" modal gets a type
// radio (szablonowy / AI) at the top, and every configured AI entry renders as a row
// with edit/remove. Storage stays on the aiNutritionPlan / aiTrainingProgram jsonb
// columns.
// TRE-1707 — those columns now hold a LIST of windows (they used to hold one object, so
// a second entry silently overwrote the first). Each window has its own id, week range and
// generation params, exactly like a template plan/diet; reads go through toAi*Plans.

const weeksLabel = (startWeek: number, endWeek: number | null) =>
  endWeek ? `od tygodnia ${startWeek} do ${endWeek}` : `od tygodnia ${startWeek} do końca`;

const NUTRITION_DEFAULTS: AiNutritionPlanConfig = {
  id: "",
  enabled: true,
  mealsSchema: 4,
  model: getDefaultAiModelId("nutritionAutomation"),
  prompt: null,
  tags: null,
  guardrails: {
    goalAdjustmentPct: { weightLoss: -15, recomposition: -10 },
    minCalories: 1400,
    maxCalories: 4000,
    proteinPerKg: 1.8,
    fatPct: 30,
  },
  startWeek: 1,
  endWeek: null,
  autoActivate: true,
  generateDishImages: false,
};

const TRAINING_DEFAULTS: AiTrainingProgramConfig = {
  id: "",
  enabled: true,
  referenceProgramIds: [],
  guidelines: null,
  model: null,
  startWeek: 1,
  endWeek: null,
  autoActivate: true,
};

/**
 * TRE-1707 — the AI columns hold a list of windows, so a save is an upsert into that list
 * (matched by config id), not a replacement of the column. `remove` drops a single window.
 */
const useSaveAiConfig = <T extends { id: string }>(field: "aiNutritionPlan" | "aiTrainingProgram") => {
  const dispatch = useAppDispatch();
  const selectedAutomation = useAppSelector((store) => store.programAutomation.selectedAutomation);
  const [saving, startSaving, stopSaving] = useLoading();

  const currentConfigs = (field === "aiNutritionPlan"
    ? toAiNutritionPlans(selectedAutomation)
    : toAiTrainingPrograms(selectedAutomation)) as unknown as T[];

  const persist = async (configs: T[], successMessage: string) => {
    if (!selectedAutomation) return;
    startSaving();
    try {
      await dispatch(
        programAutomationActions.updateAutomationTemplate({
          automationId: selectedAutomation.id,
          automation: { [field]: configs },
        }),
      ).unwrap();
      void message.success(successMessage);
    } catch {
      void message.error("Nie udało się zapisać konfiguracji");
    } finally {
      stopSaving();
    }
  };

  const save = async (config: T) => {
    const withId = config.id ? config : { ...config, id: uuid() };
    const exists = currentConfigs.some((item) => item.id === withId.id);

    await persist(
      exists ? currentConfigs.map((item) => (item.id === withId.id ? withId : item)) : [...currentConfigs, withId],
      "Zapisano konfigurację AI",
    );
  };

  const remove = async (configId: string) => {
    await persist(
      currentConfigs.filter((item) => item.id !== configId),
      "Usunięto konfigurację AI",
    );
  };

  return { save, remove, saving, selectedAutomation, currentConfigs };
};

const normalizeNutritionValues = (values: AiNutritionPlanConfig): AiNutritionPlanConfig => ({
  ...values,
  model: resolveAiModelId("nutritionAutomation", values.model),
  prompt: values.prompt || null,
  endWeek: values.endWeek ?? null,
});

const normalizeTrainingValues = (values: AiTrainingProgramConfig): AiTrainingProgramConfig => ({
  ...values,
  guidelines: values.guidelines || null,
  model: values.model ? resolveAiModelId("training", values.model) : null,
  endWeek: values.endWeek ?? null,
});

/**
 * Reference-diet picker of the AI window: the same template search, filters and option details as
 * the weight-range schedule, so a reference diet is found the way any other template is.
 */
const ReferenceMealsPlansField = ({ savedIds }: { savedIds: string[] }): ReactElement => {
  const [filter, setFilter] = useState(DEFAULT_MEALS_PLAN_TEMPLATE_FILTER);
  const { plans, isLoading, onSearch } = useMealsPlanTemplateSearch(filter, savedIds);

  return (
    <>
      <MealsPlanTemplateFilterFields value={filter} onChange={setFilter} />
      <Form.Item
        name="referenceMealsPlanIds"
        label="Diety referencyjne"
        tooltip="Diety, z których agent czerpie dania w pierwszej kolejności. Finalna dieta jest zawsze personalizowana: kaloryka i makra liczone są z ankiety klientki. Puste = agent szuka dań w całej bazie."
      >
        <MealsPlanTemplateSelect
          mode="multiple"
          plans={plans}
          onSearch={onSearch}
          loading={isLoading}
          placeholder="Szukaj diet referencyjnych..."
        />
      </Form.Item>
    </>
  );
};

const trainingModelOptions = getAiModelSelectOptions("training");

const NutritionAiFields = ({
  models,
  savedReferenceIds,
}: {
  models: { value: string; label: string }[];
  /** Ids already saved on the edited window; they stay selectable outside the current search page. */
  savedReferenceIds: string[];
}): ReactElement => (
  <>
    <Form.Item name="enabled" label="Włączone" valuePropName="checked">
      <Switch />
    </Form.Item>
    <Form.Item
      name="mealsSchema"
      label="Liczba posiłków dziennie"
      rules={[{ required: true, message: "Uzupełnij liczbę posiłków" }]}
    >
      <InputNumber min={2} max={6} precision={0} />
    </Form.Item>
    <Form.Item name="model" label="Model generacji" rules={[{ required: true, message: "Wybierz model" }]}>
      <Select options={models} />
    </Form.Item>
    <ReferenceMealsPlansField savedIds={savedReferenceIds} />
    <Form.Item name="prompt" label="Dodatkowe wytyczne (prompt)">
      <Input.TextArea rows={3} placeholder="np. lekkie kolacje, kuchnia polska, proste przepisy do 30 minut" />
    </Form.Item>

    <Form.Item label="Widelki kalkulatora" colon={false} className="mb-1">
      <Tooltip title="Deterministyczny kalkulator (Mifflin-St Jeor) liczy kcal i makra z ankiety klientki. Te pola ograniczają wynik — agent nigdy nie wymyśla kaloryki.">
        <Tag>Mifflin-St Jeor + limity</Tag>
      </Tooltip>
    </Form.Item>
    <Form.Item name={["guardrails", "goalAdjustmentPct", "weightLoss"]} label="Korekta kcal: Schudnąć (%)">
      <InputNumber min={-40} max={20} precision={0} placeholder="-15" />
    </Form.Item>
    <Form.Item name={["guardrails", "goalAdjustmentPct", "recomposition"]} label="Korekta kcal: Rzeźba (%)">
      <InputNumber min={-40} max={20} precision={0} placeholder="-10" />
    </Form.Item>
    <Form.Item name={["guardrails", "minCalories"]} label="Minimum kcal">
      <InputNumber min={1000} max={3000} precision={0} />
    </Form.Item>
    <Form.Item name={["guardrails", "maxCalories"]} label="Maksimum kcal">
      <InputNumber min={1500} max={6000} precision={0} />
    </Form.Item>
    <Form.Item name={["guardrails", "proteinPerKg"]} label="Białko (g/kg)">
      <InputNumber min={1} max={3} step={0.1} />
    </Form.Item>
    <Form.Item name={["guardrails", "fatPct"]} label="Tłuszcz (% kcal)">
      <InputNumber min={15} max={45} precision={0} />
    </Form.Item>

    <Form.Item name="startWeek" label="Od tygodnia" rules={[{ required: true, message: "Uzupełnij tydzień" }]}>
      <InputNumber min={1} precision={0} />
    </Form.Item>
    <Form.Item name="endWeek" label="Do tygodnia">
      <InputNumber min={1} precision={0} placeholder="do końca" />
    </Form.Item>
    <Form.Item
      name="autoActivate"
      label="Automatyczna aktywacja"
      valuePropName="checked"
      tooltip="Wygenerowana dieta jest od razu aktywowana klientce (bez ręcznej akceptacji trenera)."
    >
      <Switch />
    </Form.Item>
    <Form.Item
      name="generateDishImages"
      label="Zdjęcia potraw"
      valuePropName="checked"
      tooltip="Po wygenerowaniu diety automatycznie powstaną zdjęcia potraw."
    >
      <Switch />
    </Form.Item>
  </>
);

const TrainingAiFields = ({
  models,
  programOptions,
  onSearch,
  loading,
}: {
  models: { value: string; label: string }[];
  programOptions: { value: string; label: string }[];
  onSearch: (value: string) => void;
  loading: boolean;
}): ReactElement => (
  <>
    <Form.Item name="enabled" label="Włączone" valuePropName="checked">
      <Switch />
    </Form.Item>
    <Form.Item
      name="referenceProgramIds"
      label="Plany referencyjne"
      tooltip="Szablony, na których agent bazuje. Finalny plan jest zawsze personalizowany: dni z ankiety (Wybierz dni treningowe), miejsce, doświadczenie i ograniczenia klientki."
      rules={[{ required: true, message: "Wybierz co najmniej jeden plan referencyjny" }]}
    >
      <Select
        mode="multiple"
        showSearch
        filterOption={false}
        loading={loading}
        onSearch={onSearch}
        options={programOptions}
        placeholder="Szukaj szablonów planów..."
      />
    </Form.Item>
    <Form.Item name="guidelines" label="Wytyczne dla agenta">
      <Input.TextArea
        rows={3}
        placeholder="np. progresja od łagodnej, akcent na pośladki i brzuch, treningi max 40 minut"
      />
    </Form.Item>
    <Form.Item name="model" label="Model (opcjonalnie)">
      <Select options={models} allowClear placeholder="domyślny modelu taska" />
    </Form.Item>
    <Form.Item name="startWeek" label="Od tygodnia" rules={[{ required: true, message: "Uzupełnij tydzień" }]}>
      <InputNumber min={1} precision={0} />
    </Form.Item>
    <Form.Item name="endWeek" label="Do tygodnia">
      <InputNumber min={1} precision={0} placeholder="do końca" />
    </Form.Item>
    <Form.Item
      name="autoActivate"
      label="Automatyczna aktywacja"
      valuePropName="checked"
      tooltip="Wygenerowany plan jest od razu aktywowany klientce (bez ręcznej akceptacji trenera)."
    >
      <Switch />
    </Form.Item>
  </>
);

const AiRow: FunctionComponent<{
  title: string;
  detail: string;
  enabled: boolean;
  onEdit: () => void;
  onRemove: () => void;
}> = ({ title, detail, enabled, onEdit, onRemove }) => (
  <div className="mt-4 flex items-center gap-3 rounded border border-dashed border-gray-300 px-4 py-3">
    <ThunderboltOutlined />
    <Space direction="vertical" size={0} className="flex-1">
      <Space>
        <Typography.Text strong>{title}</Typography.Text>
        {enabled ? <Tag color="green">aktywne</Tag> : <Tag>wyłączone</Tag>}
      </Space>
      <Typography.Text type="secondary">{detail}</Typography.Text>
    </Space>
    <Typography.Link onClick={onEdit}>edytuj</Typography.Link>
    <Popconfirm title="Usunąć konfigurację AI?" okText="Usuń" cancelText="Anuluj" onConfirm={onRemove}>
      <Typography.Link type="danger">Usuń</Typography.Link>
    </Popconfirm>
  </div>
);

const useTrainingProgramOptions = (savedIds: string[]) => {
  const { data: programs, setSearchValue, isLoading } = useTrainingPlanSearch();
  const options = [
    ...(programs ?? []).map((program) => ({ value: program.id, label: program.name })),
    // Saved references outside the current search page stay selectable (id as label fallback).
    ...savedIds
      .filter((id) => !(programs ?? []).some((program) => program.id === id))
      .map((id) => ({ value: id, label: id })),
  ];
  return { options, setSearchValue, isLoading };
};

/** Configured AI diet windows + the edit modal of the one being edited (nutrition panel). */
export const AiNutritionPlanSection: FunctionComponent = () => {
  const { save, remove, saving, currentConfigs } = useSaveAiConfig<AiNutritionPlanConfig>("aiNutritionPlan");
  const [form] = Form.useForm<AiNutritionPlanConfig>();
  const [editedId, setEditedId] = useState<string | null>(null);
  const { models } = useMealPlanOptions();
  const configs = orderBy(currentConfigs, ["startWeek"]);
  const edited = configs.find((config) => config.id === editedId) ?? null;

  useEffect(() => {
    if (!edited) return;
    form.resetFields();
    form.setFieldsValue({
      ...edited,
      model: resolveAiModelId("nutritionAutomation", edited.model),
      prompt: edited.prompt ?? undefined,
      tags: edited.tags ?? undefined,
      referenceMealsPlanIds: edited.referenceMealsPlanIds ?? undefined,
      guardrails: edited.guardrails ?? undefined,
      endWeek: edited.endWeek ?? undefined,
    });
  }, [edited, form]);

  if (!configs.length) return null;

  return (
    <>
      {configs.map((config) => (
        <AiRow
          key={config.id}
          title="AI dieta (v2) — generowana agentem"
          detail={[
            weeksLabel(config.startWeek, config.endWeek),
            `${config.mealsSchema} posiłki`,
            config.model,
            ...(config.referenceMealsPlanIds?.length
              ? [`${config.referenceMealsPlanIds.length} diet(y) referencyjne`]
              : []),
          ].join(" · ")}
          enabled={config.enabled}
          onEdit={() => setEditedId(config.id)}
          onRemove={() => void remove(config.id)}
        />
      ))}
      <Modal
        open={Boolean(edited)}
        title="AI dieta (v2) — generowana agentem"
        footer={null}
        width={720}
        onCancel={() => setEditedId(null)}
        destroyOnClose
      >
        <Form<AiNutritionPlanConfig>
          form={form}
          layout="horizontal"
          labelCol={{ span: 9 }}
          wrapperCol={{ span: 12 }}
          initialValues={NUTRITION_DEFAULTS}
          onFinish={(values) =>
            void save({ ...normalizeNutritionValues(values), id: edited?.id ?? "" }).then(() => setEditedId(null))
          }
        >
          <NutritionAiFields models={models} savedReferenceIds={edited?.referenceMealsPlanIds ?? []} />
          <Form.Item wrapperCol={{ offset: 9 }}>
            <Button type="primary" htmlType="submit" loading={saving}>
              Zapisz harmonogram AI
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

/** Configured AI program windows + the edit modal of the one being edited (programs panel). */
export const AiTrainingProgramSection: FunctionComponent = () => {
  const { save, remove, saving, currentConfigs } = useSaveAiConfig<AiTrainingProgramConfig>("aiTrainingProgram");
  const [form] = Form.useForm<AiTrainingProgramConfig>();
  const [editedId, setEditedId] = useState<string | null>(null);
  const models = trainingModelOptions;
  const configs = orderBy(currentConfigs, ["startWeek"]);
  const edited = configs.find((config) => config.id === editedId) ?? null;
  const { options, setSearchValue, isLoading } = useTrainingProgramOptions(edited?.referenceProgramIds ?? []);

  useEffect(() => {
    if (!edited) return;
    form.resetFields();
    form.setFieldsValue({
      ...edited,
      guidelines: edited.guidelines ?? undefined,
      model: edited.model ? resolveAiModelId("training", edited.model) : undefined,
      endWeek: edited.endWeek ?? undefined,
    });
  }, [edited, form]);

  if (!configs.length) return null;

  return (
    <>
      {configs.map((config) => (
        <AiRow
          key={config.id}
          title="AI plan treningowy (v2) — generowany agentem"
          detail={`${weeksLabel(config.startWeek, config.endWeek)} · ${config.referenceProgramIds.length} plan(y) referencyjne`}
          enabled={config.enabled}
          onEdit={() => setEditedId(config.id)}
          onRemove={() => void remove(config.id)}
        />
      ))}
      <Modal
        open={Boolean(edited)}
        title="AI plan treningowy (v2) — generowany agentem"
        footer={null}
        width={720}
        onCancel={() => setEditedId(null)}
        destroyOnClose
      >
        <Form<AiTrainingProgramConfig>
          form={form}
          layout="horizontal"
          labelCol={{ span: 9 }}
          wrapperCol={{ span: 12 }}
          initialValues={TRAINING_DEFAULTS}
          onFinish={(values) =>
            void save({ ...normalizeTrainingValues(values), id: edited?.id ?? "" }).then(() => setEditedId(null))
          }
        >
          <TrainingAiFields models={models} programOptions={options} onSearch={setSearchValue} loading={isLoading} />
          <Form.Item wrapperCol={{ offset: 9 }}>
            <Button type="primary" htmlType="submit" loading={saving}>
              Zapisz plan AI
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

type AddNutritionScheduleFormProps = {
  formController?: FormInstance;
  onSubmitTemplate: Parameters<typeof ScheduledNutritionForm>[0]["onSubmit"];
  selectedAutomation: Parameters<typeof ScheduledNutritionForm>[0]["selectedAutomation"];
  onAiSaved: () => void;
};

/**
 * The single "Dodaj kolejny harmonogram" modal body: type radio at the top,
 * template (weight-range) form or the AI (v2) form below. ModalForm's OK button
 * submits whichever form is bound to the shared controller.
 */
export const AddNutritionScheduleForm: FunctionComponent<AddNutritionScheduleFormProps> = ({
  formController,
  onSubmitTemplate,
  selectedAutomation,
  onAiSaved,
}) => {
  const [mode, setMode] = useState<"template" | "ai">("template");
  const { save } = useSaveAiConfig<AiNutritionPlanConfig>("aiNutritionPlan");
  const { models } = useMealPlanOptions();

  return (
    <>
      <Radio.Group
        value={mode}
        onChange={(event) => {
          formController?.resetFields();
          setMode(event.target.value as "template" | "ai");
        }}
        className="mb-6"
      >
        <Radio.Button value="template">Szablonowy (zakresy wagowe)</Radio.Button>
        <Radio.Button value="ai">AI (v2) — generowany agentem</Radio.Button>
      </Radio.Group>

      {mode === "template" ? (
        <ScheduledNutritionForm
          formController={formController}
          onSubmit={onSubmitTemplate}
          selectedAutomation={selectedAutomation}
        />
      ) : (
        // a fresh id on every submit — this appends a window, it never replaces an existing one
        <Form<AiNutritionPlanConfig>
          form={formController as FormInstance<AiNutritionPlanConfig>}
          layout="horizontal"
          labelCol={{ span: 9 }}
          wrapperCol={{ span: 12 }}
          initialValues={NUTRITION_DEFAULTS}
          onFinish={(values) => void save({ ...normalizeNutritionValues(values), id: uuid() }).then(onAiSaved)}
        >
          <NutritionAiFields models={models} savedReferenceIds={[]} />
        </Form>
      )}
    </>
  );
};

type AddTrainingScheduleFormProps = {
  formController?: FormInstance;
  /** Called when the user confirms the "szablonowy" mode — opens the legacy stepper. */
  onTemplateChosen: () => void;
  onAiSaved: () => void;
};

/**
 * The single "Dodaj kolejny plan" modal body: template mode hands off to the
 * legacy stepper on OK, AI mode renders the AI (v2) form inline.
 */
export const AddTrainingScheduleForm: FunctionComponent<AddTrainingScheduleFormProps> = ({
  formController,
  onTemplateChosen,
  onAiSaved,
}) => {
  const [mode, setMode] = useState<"template" | "ai">("template");
  const { save } = useSaveAiConfig<AiTrainingProgramConfig>("aiTrainingProgram");
  const models = trainingModelOptions;
  const { options, setSearchValue, isLoading } = useTrainingProgramOptions([]);

  return (
    <>
      <Radio.Group
        value={mode}
        onChange={(event) => {
          formController?.resetFields();
          setMode(event.target.value as "template" | "ai");
        }}
        className="mb-6"
      >
        <Radio.Button value="template">Szablonowy (kreator)</Radio.Button>
        <Radio.Button value="ai">AI (v2) — generowany agentem</Radio.Button>
      </Radio.Group>

      <Form
        form={formController}
        layout="horizontal"
        labelCol={{ span: 9 }}
        wrapperCol={{ span: 12 }}
        initialValues={TRAINING_DEFAULTS}
        onFinish={(values) => {
          if (mode === "template") {
            onTemplateChosen();
            return;
          }
          // a fresh id on every submit — this appends a window, it never replaces an existing one
          void save({ ...normalizeTrainingValues(values as AiTrainingProgramConfig), id: uuid() }).then(onAiSaved);
        }}
      >
        {mode === "template" ? (
          <Alert
            type="info"
            showIcon
            message="Po zatwierdzeniu otworzy się kreator wyboru szablonu planu (jak dotychczas)."
          />
        ) : (
          <TrainingAiFields models={models} programOptions={options} onSearch={setSearchValue} loading={isLoading} />
        )}
      </Form>
    </>
  );
};
